import { useEffect, useState } from 'react'
import { useLanguage } from '@/localization'
import {
  formatLocalizedDate,
  getInitialLanguage,
  newsTagLabel,
} from '../../../../shared/localization/core'

/** Normalized news item used across the MC site. */
export interface NewsItem {
  id: string
  title: string
  excerpt?: string
  body?: string
  tag?: string
  link?: string | null
  pinned?: boolean
  date?: string
}

/** Raw API shape (tolerant — fields may use alternate names). */
interface RawNewsItem {
  id?: string | number
  title?: string
  excerpt?: string
  summary?: string
  body?: string
  tag?: string
  link?: string | null
  url?: string | null
  pinned?: boolean
  published?: boolean
  created_at?: string
  date?: string
}

const MC_TAG = 'minecraft'

/** Map a raw API item to the normalized NewsItem shape. */
function normalize(item: RawNewsItem, index: number): NewsItem {
  return {
    id: String(item.id ?? index),
    title: item.title ?? 'Untitled',
    excerpt: item.excerpt ?? item.summary,
    body: item.body,
    tag: item.tag,
    link: item.link ?? item.url ?? null,
    pinned: Boolean(item.pinned),
    date: item.date ?? item.created_at,
  }
}

/** Pull the array of news items out of whatever envelope the API returns. */
function extractList(data: unknown): RawNewsItem[] {
  if (Array.isArray(data)) return data as RawNewsItem[]
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (Array.isArray(obj.items)) return obj.items as RawNewsItem[]
    if (Array.isArray(obj.news)) return obj.news as RawNewsItem[]
  }
  return []
}

/** Sort pinned-first, then newest-first by date. */
function sortNews(items: NewsItem[]): NewsItem[] {
  return [...items].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1
    const da = a.date ? Date.parse(a.date) : 0
    const db = b.date ? Date.parse(b.date) : 0
    return db - da
  })
}

export type NewsListState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'empty' }
  | { status: 'ready'; items: NewsItem[] }

/**
 * Fetches the Minecraft-tagged news list (`GET /api/news?tag=minecraft`,
 * proxied to :8078 in dev) once on mount. Items come back sorted pinned-first
 * then newest-first.
 */
export function useNews(): NewsListState {
  const { apiPath } = useLanguage()
  const [state, setState] = useState<NewsListState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    setState({ status: 'loading' })

    async function load() {
      try {
        const res = await fetch(apiPath(`/api/news?tag=${MC_TAG}`), {
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        if (cancelled) return
        const items = sortNews(extractList(data).map(normalize))
        setState(items.length ? { status: 'ready', items } : { status: 'empty' })
      } catch (err) {
        if (cancelled || (err as Error).name === 'AbortError') return
        setState({ status: 'error' })
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [apiPath])

  return state
}

export type NewsItemState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'notfound' }
  | { status: 'ready'; item: NewsItem }

/**
 * Fetches a single news item by id (`GET /api/news/:id`). Distinguishes a 404
 * (post missing/unpublished) from a transient network error.
 */
export function useNewsItem(id: string | undefined): NewsItemState {
  const { apiPath } = useLanguage()
  const [state, setState] = useState<NewsItemState>({ status: 'loading' })

  useEffect(() => {
    if (!id) {
      setState({ status: 'notfound' })
      return
    }

    let cancelled = false
    const controller = new AbortController()
    setState({ status: 'loading' })

    async function load() {
      try {
        const res = await fetch(apiPath(`/api/news/${encodeURIComponent(id as string)}`), {
          signal: controller.signal,
        })
        if (res.status === 404) {
          if (!cancelled) setState({ status: 'notfound' })
          return
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        if (cancelled) return
        const raw =
          data && typeof data === 'object' && 'item' in data
            ? (data as { item: RawNewsItem }).item
            : data && typeof data === 'object' && 'news' in data
              ? (data as { news: RawNewsItem }).news
              : (data as RawNewsItem)
        setState({ status: 'ready', item: normalize(raw, 0) })
      } catch (err) {
        if (cancelled || (err as Error).name === 'AbortError') return
        setState({ status: 'error' })
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [apiPath, id])

  return state
}

/** Format an ISO date string into a friendly label; falls back to the raw value. */
export function formatNewsDate(date?: string): string | undefined {
  return formatLocalizedDate(date, getInitialLanguage()) || undefined
}

export { newsTagLabel }
